import { useMemo } from "react";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import { isVreDocument } from "@wooglobe/vre-editor";
import { apiErrorText, fetchEditorTemplate, toEditorTemplate } from "./EditorTemplateGallery";

// Shared by the single-video editor dialog and the batch editor, so both pick
// the same source file, the same template, and save the same payload shape.

/**
 * Who is signed in, so the editor's saved caption prompt belongs to THIS
 * person and not to this browser. `state.user` is the decoded access token,
 * so `user_id` is SimpleJWT's own claim; the string fallbacks only matter if
 * a token is ever issued without it. Undefined when signed out, which the
 * editor reads as "no identity" rather than guessing one.
 *
 * Right after a page refresh `state.user` is still null while the app checks
 * the cookies, so the token is read straight from its cookie meanwhile —
 * otherwise an editor opened in that window had nobody's prompt.
 */
export function useEditorUserKey() {
  const currentUser = useSelector((state) => state.user);
  return useMemo(() => {
    const user = currentUser ?? userFromCookie();
    const id = user?.user_id ?? user?.username ?? user?.email;
    return id === undefined || id === null || id === "" ? undefined : String(id);
  }, [currentUser]);
}

function userFromCookie() {
  const token = Cookies.get("access_token");
  if (!token || token === "undefined") return null;
  try {
    return jwt_decode(token);
  } catch {
    return null;
  }
}

// ── Video delivery origin ────────────────────────────────────────────────────
// The wooglobe bucket sits behind a CloudFront distribution, and the two are
// interchangeable per object: same path, different host.
//
// This matters for rendering: the render worker downloads the whole source file,
// and origin S3 in us-west-2 is dramatically slower than the CDN for large
// masters — measured on one 439 MB raw .mov: ~0.9 MB/s direct from S3 (~8.4 min)
// vs ~7.9 MB/s through CloudFront (~1 min) on a cache MISS.
//
// These convert in BOTH directions on purpose, so picking "CloudFront URL (CDN)"
// never leaves an already-S3 URL on the slow origin.
const S3_HOST = "wooglobe.s3.us-west-2.amazonaws.com";
const CLOUDFRONT_HOST = "dvkno8lo5mn14.cloudfront.net";

const toS3Url = (url) => (url ? url.replace(CLOUDFRONT_HOST, S3_HOST) : url);
const toCloudFrontUrl = (url) => (url ? url.replace(S3_HOST, CLOUDFRONT_HOST) : url);

/**
 * The video's own source file. WooGlobe search results carry `raw_video_url`;
 * compilations and native (Video Library) videos have no such field, only the
 * file they were uploaded as (`s3_url`). Other videos never fall back to it —
 * that would silently edit a watermarked copy instead of the raw source.
 */
export const editSourceUrl = (video) =>
  video?.raw_video_url ||
  (video?.is_compilation || video?.is_native_video ? video?.s3_url || video?.original_video_url : undefined);

/** The URL to hand a downloader, honouring the user's origin choice. */
export const resolveDeliveryUrl = (url, urlType) => (urlType === "s3" ? toS3Url(url) : toCloudFrontUrl(url));

/**
 * A single clip's length and frame size, read the same way useVideoMetadata
 * does (a throw-away <video preload="metadata">), but as a one-shot Promise
 * so several raw clips can be probed together with Promise.all — the editor
 * needs every clip's metadata up front to build the merged document, not one
 * at a time reactively.
 */
export const probeVideoMetadata = (url) =>
  new Promise((resolve) => {
    const vid = document.createElement("video");
    vid.preload = "metadata";
    let timer = null;
    const finish = (ok, duration = 0, width = 0, height = 0) => {
      clearTimeout(timer);
      vid.onloadedmetadata = null;
      vid.onerror = null;
      releaseVideo(vid);
      resolve({ ok, duration, width, height });
    };
    vid.onloadedmetadata = () =>
      finish(true, Number.isFinite(vid.duration) ? vid.duration : 0, vid.videoWidth || 0, vid.videoHeight || 0);
    vid.onerror = () => finish(false);
    // A clip that never answers must not hold the editor on "Loading…" forever.
    timer = setTimeout(() => finish(false), PROBE_TIMEOUT_MS);
    vid.src = url;
  });

/** How long reading a clip's length and size may take before it counts as failed. */
export const PROBE_TIMEOUT_MS = 20000;

/**
 * Stop a throw-away <video> downloading and free its connection. `src = ""`
 * alone does not: the browser keeps the request open, and over HTTP/1.1 six
 * such probes use up every connection to that host, so the next clip (and the
 * editor waiting on it) never loads. Removing the source and calling load()
 * is the documented way to release the media resource.
 */
export const releaseVideo = (vid) => {
  vid.removeAttribute("src");
  try {
    vid.load();
  } catch (_) {
    /* nothing left to release */
  }
};

/** True when an element still points at a file only this browser tab can reach. */
export const hasLocalMedia = (doc) =>
  (doc?.elements || []).some((el) => typeof el?.source === "string" && el.source.startsWith("blob:"));

// ── Templates ────────────────────────────────────────────────────────────────

/**
 * A brand's assigned template row (`/mrss/brand-templates/`) in the shape the
 * editor opens. Built whenever a template is assigned, even with an empty
 * snapshot: the editor fetches the current template and only falls back to it.
 */
export const asPreselected = (assigned) =>
  assigned?.template_id
    ? { id: assigned.template_id, name: assigned.template_name, document: assigned.template_snapshot }
    : null;

/** The VRE entry of a saved edit payload, or null when it is not one. */
const savedVreEntry = (savedPayload) => {
  const saved = Array.isArray(savedPayload) ? savedPayload[0] : null;
  return saved && saved.engine === "vre" && isVreDocument(saved.document) ? saved : null;
};

/** The template a saved edit was made on, or null when there is no usable one. */
export const savedTemplateIdOf = (savedPayload) => {
  const saved = savedVreEntry(savedPayload);
  return saved && saved.template_id ? String(saved.template_id) : null;
};

/**
 * Only resume onto the template the edit was made on; a different template
 * starts fresh, since the saved layers belong to the old one. A newer version
 * of the same template still resumes: the user's edits matter more.
 */
export const resumeDocumentFor = (savedPayload, template) => {
  const saved = savedVreEntry(savedPayload);
  if (!saved || !template) return null;
  return String(saved.template_id ?? "") === String(template.id ?? "") ? saved.document : null;
};

/**
 * Which template to open. A saved edit reopens on the template it was made on,
 * even one picked from the gallery. Otherwise a landscape video takes the
 * brand's horizontal template when there is one, else the vertical one, or
 * whichever single template the brand assigned — the editor frames a
 * mismatched video over a blurred copy of itself.
 */
export const pickStartTemplate = ({ savedPayload, landscape, vertical, horizontal }) => {
  const savedId = savedTemplateIdOf(savedPayload);
  if (savedId) return { id: savedId, document: savedPayload[0].document };
  return (landscape && horizontal) || vertical || horizontal || null;
};

/**
 * Loads `pre` (`{id, name?, document?}`) as an editor template: the current
 * version from the server, else the stored snapshot when that is a VRE
 * document. Resolves `{template}` or `{notice}` explaining why the gallery has
 * to be used instead. `fetchTemplate` lets a caller cache the detail requests.
 */
export async function loadStartTemplate(pre, fetchTemplate = fetchEditorTemplate) {
  let loadError = "";
  if (pre.id) {
    try {
      const detail = await fetchTemplate(pre.id);
      if (isVreDocument(detail?.document)) return { template: toEditorTemplate(detail, pre) };
    } catch (err) {
      loadError = apiErrorText(err, "request failed");
    }
  }
  if (isVreDocument(pre.document)) {
    return {
      template: { id: String(pre.id ?? ""), name: pre.name || "Template", version: null, document: pre.document, fallback: true },
    };
  }
  // A stored snapshot that is not a VRE document came from the old editor;
  // the server answers 404 for those, so the snapshot decides the message.
  const madeForOldEditor = Boolean(pre.document) || !loadError;
  const label = pre.name ? `"${pre.name}"` : "the brand's template";
  return {
    notice: madeForOldEditor
      ? `This brand's template (${label}) was made for the old editor — choose a template.`
      : `Could not load ${label} (${loadError}) — choose a template.`,
  };
}

/** The edit payload stored on a platform config as its `renderPayload`. */
export const buildRenderPayload = (template, doc) => [
  {
    engine: "vre",
    template_id: template.id || "",
    template_version: typeof template.version === "number" ? template.version : null,
    document: doc,
  },
];
